import { ChangeEvent, useCallback, useRef } from 'react';
import { ExcelGridConfig, ExcelUploadContext } from 'src/utils/excel/excelGridConfig';
import {
	buildHeaderKeyMap,
	downloadExcelTemplate,
	getNextValueIndexes,
	mapRowFromExcel,
	parseExcelFile,
	validateRequiredHeaders,
} from 'src/utils/excel/excelTemplateUtil';
import { showWarning } from 'src/utils/toast/toastUtil';

export const useGridExcelUpload = <T extends object>(
	config: ExcelGridConfig<T>,
	existingList: T[],
	setDataList: (data: T[]) => void,
	context: ExcelUploadContext = { existingList }
) => {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleDownloadTemplate = useCallback(() => {
		try {
			downloadExcelTemplate({
				columnOrder: config.columnOrder,
				headerNames: config.headerNames,
				colWidths: config.template.colWidths,
				sheetName: config.template.sheetName,
				fileNamePrefix: config.template.fileNamePrefix,
			});
		} catch (error) {
			console.error('템플릿 다운로드 실패:', error);
			alert('템플릿 다운로드 중 오류가 발생했습니다.');
		}
	}, [config]);

	const handleUploadButtonClick = useCallback(() => {
		const validationMessage = config.validateBeforeUpload?.();
		if (validationMessage) {
			alert(validationMessage);
			return;
		}
		fileInputRef.current?.click();
	}, [config]);

	const handleExcelUpload = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) {
			if (event.target) event.target.value = '';
			return;
		}

		const uploadContext: ExcelUploadContext = {
			...context,
			existingList,
		};

		const validationMessage = config.validateBeforeUpload?.();
		if (validationMessage) {
			alert(validationMessage);
			event.target.value = '';
			return;
		}

		try {
			const json = await parseExcelFile(file);

			if (json.length === 0) {
				alert('엑셀 파일에 데이터가 없습니다.');
				return;
			}

			if (config.requiredHeaders?.length) {
				const excelHeaders = Object.keys(json[0]);
				const missingHeaders = validateRequiredHeaders(excelHeaders, config.requiredHeaders);
				if (missingHeaders.length > 0) {
					alert(`업로드 실패: 필수 헤더가 누락되었습니다.\n- ${missingHeaders.join('\n- ')}`);
					return;
				}
			}

			const headerKeyMap = buildHeaderKeyMap(config.headerNames);
			const valueIndexes = getNextValueIndexes(existingList, json.length);
			const newRows: T[] = [];

			for (let index = 0; index < json.length; index++) {
				let row = mapRowFromExcel(
					json[index],
					headerKeyMap,
					config.defaultRow,
					config.valueTransformers
				);

				if (config.processRow) {
					row = await config.processRow(row, index, uploadContext);
				}

				const defaultMeta = {
					valueIndex: valueIndexes[index],
					inserted: true,
					isModified: true,
				};
				const rowMeta = config.createRowMeta?.(row, index, valueIndexes[index], uploadContext) ?? defaultMeta;

				newRows.push({ ...row, ...rowMeta });
			}

			if (config.uploadMode === 'replace') {
				setDataList(newRows);
			} else {
				setDataList([...existingList, ...newRows]);
			}
		} catch (error) {
			console.error('엑셀 업로드 실패:', error);
			if (config.onUploadError) {
				config.onUploadError(error);
				return;
			}
			if (error instanceof Error && error.message.startsWith('엑셀')) {
				showWarning(error.message);
				return;
			}
			alert('엑셀 업로드 중 오류가 발생했습니다.');
		} finally {
			event.target.value = '';
		}
	}, [config, context, existingList, setDataList]);

	return {
		fileInputRef,
		handleDownloadTemplate,
		handleUploadButtonClick,
		handleExcelUpload,
	};
};
